import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withKeys as _withKeys, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-2a952d04"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "container" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", null, "Verify your email", -1))
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "subheading" }, "Enter the 6-digit code sent to your email", -1))
const _hoisted_4 = {
  key: 0,
  class: "error"
}
const _hoisted_5 = { class: "navButtons" }

import HeyButton from "@/components/HeyButton.vue";
import BackIcon from "@/components/Icons/BackIcon.vue";
import ErrorIcon from "@/components/Icons/ErrorIcon.vue";
import NextIcon from "@/components/Icons/NextIcon.vue";
import LogoIcon from "@/components/Icons/LogoIcon.vue";
import router from "@/router";
import axios from "axios";

import { computed, onMounted, ref } from "vue";

import { useStore } from "vuex";

// Vuex Store

export default /*#__PURE__*/_defineComponent({
  __name: 'VerifyView',
  setup(__props) {

// Imports
const store = useStore();
computed(() => store.state.authStatus);

// Reactive Variables/Props
const verificationCodeForm = ref<HTMLFormElement | null>(null);
const verifying = ref(false);
const error = ref("");

// Functions
function isDigit(n: string): boolean {
  return /^\d$/.test(n);
}

function getUserTypedCode(): string {
  const inputBoxes = verificationCodeForm.value?.children as HTMLCollection;
  let code = "";

  for (let i = 0; i < inputBoxes.length; i++) {
    const inputBox = inputBoxes[i] as HTMLInputElement;
    code += inputBox.value;
  }

  return code;
}

async function handleDelete(event: Event) {
  const e = event as KeyboardEvent;
  const currentInput = e.target as HTMLInputElement;
  const inputBeforeCurrent =
    currentInput.previousElementSibling as HTMLInputElement | null;

  e.preventDefault();
  currentInput.value = "";

  if (inputBeforeCurrent) {
    inputBeforeCurrent.focus();
    inputBeforeCurrent.select();
  }
}

async function handlePaste(event: Event) {
  const e = event as ClipboardEvent;
  e.preventDefault();

  if (!e.clipboardData) return; // Stop function if clipboard data doesn't exist

  const pastedData = e.clipboardData.getData("text/plain").trim();

  // Insert data into verification code fields if number 6-digits or less is pasted
  if (/^\d{1,6}$/.test(pastedData)) {
    const inputBoxes = verificationCodeForm.value?.children as HTMLCollection;

    for (let i = 0; i < inputBoxes.length; i++) {
      const inputBox = inputBoxes[i] as HTMLInputElement;
      inputBox.value = "";

      if (pastedData[i]) {
        inputBox.value = pastedData[i];
        inputBox.focus();
      }
    }

    if (pastedData.length < 6) {
      const nextInputBox = inputBoxes[pastedData.length] as HTMLInputElement;
      nextInputBox.focus();
    } else if (pastedData.length == 6) {
      error.value = "";
      submitCode();
    }
  }
}

async function handleInput(event: Event) {
  const e = event as InputEvent;
  const dataInputted = e.data || "";
  const currentInput = e.target as HTMLInputElement;
  const oldValue = currentInput.value.replace(dataInputted, "");

  // Stop any invalid input conditions from happening
  if (dataInputted.length != 1 || !isDigit(dataInputted)) {
    currentInput.value = oldValue;
    return;
  }

  currentInput.value = dataInputted;
  error.value = "";

  // Shift focus to next input UNLESS currently focused input is the 6th (last) input
  const nextInput = currentInput.nextElementSibling as HTMLInputElement;
  if (nextInput) {
    nextInput.focus();
    nextInput.select();
  }
}

async function submitCode() {
  // Don't allow duplicate requests to be sent if already logging in or if there an unaddressed error
  if (verifying.value || error.value) return;

  // Pause script so that user can be spinner, even if response from server is fast
  verifying.value = true;
  await sleep(500);

  // Show error if not all input boxes are filled
  const code = getUserTypedCode();
  if (code.length != 6) {
    verifying.value = false;
    return (error.value = "You must fill every digit");
  }

  try {
    const res = await axios.post("/sessions/verify", { code: parseInt(code) });
    verifying.value = false;

    // Get friends
    const { data } = await axios.get("/friendships/all");
    store.dispatch("addAllFriendships", data);

    // Verify session
    store.dispatch("verifySession", res.data);

    // Send to overview
    router.push("/overview");
  } catch (err) {
    verifying.value = false;
    error.value = "Incorrect or Expired Verification Code";
  }
}

async function cancelVerification() {
  await axios.delete("/sessions/delete");
  store.dispatch("logout");
  router.push("/auth");
}

// Lifecycle Hooks
onMounted(() => {
  const firstInputBox = verificationCodeForm.value?.children[0] as HTMLInputElement;

  firstInputBox.autocomplete = "one-time-code";
  firstInputBox.focus();
});

async function sleep(milliseconds: number) {
  await new Promise((resolve) => {
    setTimeout(() => {
      resolve("done");
    }, milliseconds);
  });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(LogoIcon, { style: {"height":"1.5em"} }),
    _hoisted_2,
    _hoisted_3,
    _createElementVNode("form", {
      ref_key: "verificationCodeForm",
      ref: verificationCodeForm
    }, [
      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(6, (n) => {
        return _createElementVNode("input", {
          key: n,
          pattern: "[0-9]*",
          onInput: handleInput,
          onPaste: handlePaste,
          onKeydown: [
            _withKeys(handleDelete, ["delete"]),
            _withKeys(submitCode, ["enter"])
          ],
          required: "",
          class: _normalizeClass(error.value ? 'error' : '')
        }, null, 34)
      }), 64))
    ], 512),
    (error.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_4, [
          _createElementVNode("span", null, [
            _createVNode(ErrorIcon)
          ]),
          _createTextVNode(" " + _toDisplayString(error.value), 1)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(HeyButton, {
        type: "secondary",
        onClick: cancelVerification
      }, {
        default: _withCtx(() => [
          _createVNode(BackIcon),
          _createTextVNode("Cancel")
        ]),
        _: 1
      }),
      _createVNode(HeyButton, {
        loading: verifying.value,
        onClick: submitCode
      }, {
        default: _withCtx(() => [
          _createTextVNode("Verify"),
          _createVNode(NextIcon)
        ]),
        _: 1
      }, 8, ["loading"])
    ])
  ]))
}
}

})